<template>
  <div ref="searchComponent" class="relative w-full" :class="{ 'shadow-lg rounded-2xl': dropdownOpen }" @keydown.tab="handleTabKey">
    <!-- Inline search input -->
    <div
      class="flex items-center h-10 border bg-white transition-colors"
      :class="dropdownOpen ? 'rounded-t-2xl border-gray-200 border-b-gray-100' : 'rounded-3xl border-gray-300'"
    >
      <div class="pl-3 flex items-center shrink-0">
        <svg
          class="h-5 w-5 text-gray-400"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fill-rule="evenodd"
            d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
            clip-rule="evenodd"
          />
        </svg>
      </div>
      <span
        v-if="activeTypeFilter"
        class="shrink-0 inline-flex items-center gap-1 ml-2 px-2 py-0.5 text-xs font-medium bg-primary-50 text-primary-700 rounded-lg border border-primary-200"
      >
        {{ formatEntityTypeName(activeTypeFilter) }}
        <button
          type="button"
          :aria-label="'Remove ' + activeTypeFilter + ' filter'"
          class="text-primary-400 hover:text-primary-600"
          @click="clearTypeFilter"
        >
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </span>
      <input
        ref="modalSearchInput"
        v-model="search_text" data-demo-search
        type="text"
        role="combobox"
        :aria-keyshortcuts="isMac ? 'Meta+k' : 'Control+k'"
        aria-autocomplete="list"
        :aria-expanded="dropdownOpen"
        aria-controls="search-results-listbox"
        :aria-activedescendant="activeDescendantId"
        :placeholder="activeTypeFilter ? 'Search ' + formatEntityTypeName(activeTypeFilter) + (activeTypeFilter.endsWith('s') ? '...' : 's...') : 'Search entities...'"
        class="sensitive w-full h-full pl-2 pr-2 bg-transparent text-sm text-gray-900 placeholder-gray-400 focus:outline-none rounded-3xl"
        @input="onSearch"
        @focus="onInputFocus"
        @keydown="handleSearchKeydown"
      />
      <div class="pr-2 flex items-center gap-1 shrink-0">
        <button
          v-if="search_text || activeTypeFilter"
          type="button"
          aria-label="Clear search"
          class="p-1 text-gray-400 hover:text-gray-600"
          @click="clearSearch"
        >
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <span
          v-if="tabCompleteSuggestion && !activeTypeFilter && dropdownOpen"
          aria-hidden="true"
          class="px-1.5 py-0.5 text-[10px] font-medium text-primary-500 bg-primary-50 border border-primary-200 rounded"
        >
          Tab
        </span>
        <span v-else aria-hidden="true" class="px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-500 rounded-xl border border-gray-200 pointer-events-none">
          {{ isMac ? '⌘' : 'Ctrl' }} K
        </span>
      </div>
    </div>

    <!-- Dropdown results -->
    <Transition name="search-dropdown">
      <div
        v-if="dropdownOpen"
        ref="modalContainer"
        class="absolute top-full left-0 right-0 z-[100] bg-white rounded-b-2xl border border-t-0 border-gray-200 overflow-hidden"
      >

            <!-- Type filter control: Practice + TIN pinned inline, plus an "N more"
                 control for the rest. Lives above the results, as its own persistent
                 row directly under the search input, in every state (recents, empty,
                 results) — including while a filter is active, so switching to a
                 different type never requires clearing first. -->
            <div
              v-if="entityTypes.length > 0"
              class="px-5 py-2.5 border-b border-gray-100"
            >
              <EntityTypeFilters
                :types="entityTypes"
                :active-type="activeTypeFilter"
                @select="setTypeFilter($event, true)"
                @clear="clearTypeFilter"
              />
            </div>

            <!-- Type autocomplete suggestions -->
            <div
              v-if="typeAutocompleteSuggestions.length > 0 && !activeTypeFilter"
              class="border-b border-gray-100 px-5 py-2"
            >
              <div class="flex items-center gap-1.5 flex-wrap">
                <span class="text-xs text-gray-400 mr-1">Filter by:</span>
                <button
                  v-for="t in typeAutocompleteSuggestions"
                  :key="t.id"
                  type="button"
                  class="px-2.5 py-1 text-xs font-medium rounded-full border border-gray-200 bg-white text-gray-600 hover:border-primary-300 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                  @click="setTypeFilter(t.type_name)"
                >
                  <font-awesome-icon
                    :icon="getEntityIcon({ entity_type_name: t.type_name })"
                    class="w-3 h-3 mr-1 text-gray-400"
                    aria-hidden="true"
                  />
                  {{ formatEntityTypeName(t.type_name) }}
                </button>
              </div>
            </div>

            <!-- Results area -->
            <div
              id="search-results-listbox"
              ref="resultsContainer"
              role="listbox"
              aria-label="Search results"
              class="max-h-[40vh] overflow-y-auto"
            >
              <!-- Loading with no previous results -->
              <div v-if="isLoading && previousResults.length === 0 && !showRecents" class="flex items-center justify-center py-12">
                <svg class="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>

              <!-- Recent entities (when no query) -->
              <div v-else-if="showRecents">
                <div class="px-5 py-1.5 flex items-center justify-between bg-gray-50 sticky top-0">
                  <span class="text-xs font-semibold text-gray-400 uppercase tracking-wide">Recent</span>
                  <button
                    type="button"
                    class="text-xs text-gray-400 hover:text-gray-600"
                    @click="clearRecents"
                  >
                    Clear
                  </button>
                </div>
                <div
                  v-for="(entity, index) in recentEntities"
                  :id="'search-result-recent-' + entity.id"
                  :key="'recent-' + entity.id"
                  role="option"
                  :aria-selected="selectedIndex === index"
                  class="flex items-center px-5 py-2.5 cursor-pointer transition-colors"
                  :class="selectedIndex === index ? 'bg-primary-50' : 'hover:bg-gray-50'"
                  @click="handleClick(entity)"
                  @mouseover="selectedIndex = index"
                >
                  <font-awesome-icon
                    :icon="getEntityIcon(entity)"
                    class="w-4 h-4 text-gray-400 shrink-0 mr-3"
                    aria-hidden="true"
                  />
                  <div class="flex flex-col min-w-0 flex-1">
                    <span class="sensitive text-sm font-medium text-gray-800 truncate">{{ entity.name }}</span>
                    <span
                      v-if="entity.breadcrumb && entity.breadcrumb.length > 0"
                      class="text-xs text-gray-400 truncate mt-0.5 inline-flex items-center gap-0.5"
                    >
                      <template v-for="(ancestor, bIdx) in entity.breadcrumb" :key="'recent-bc-' + bIdx">
                        <span v-if="bIdx > 0" class="text-gray-300 mx-0.5">›</span>
                        <font-awesome-icon
                          :icon="getEntityIcon({ entity_type_name: ancestor.type_name })"
                          class="w-2.5 h-2.5 opacity-50 shrink-0"
                          aria-hidden="true"
                        />
                        <span class="sensitive truncate">{{ ancestor.name }}</span>
                      </template>
                    </span>
                  </div>
                  <span class="shrink-0 ml-3 px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-500 rounded-full">
                    {{ formatEntityTypeName(entity.entity_type_name || 'Entity') }}
                  </span>
                  <button
                    type="button"
                    :aria-label="'Remove ' + entity.name + ' from recents'"
                    class="sensitive shrink-0 ml-2 p-1 text-gray-300 hover:text-gray-500 transition-colors"
                    @click.stop="removeRecent(entity.id)"
                  >
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <!-- Empty prompt (no query, no recents, no type filter) -->
              <div v-else-if="(!search_text || search_text.length < 2) && !activeTypeFilter" class="px-5 py-8">
                <p class="text-sm text-gray-400 text-center mb-4">Type 2+ characters to search</p>
              </div>

              <!-- No API results but matching recents -->
              <div v-else-if="displayResults.length === 0 && !isLoading && matchingRecents.length > 0">
                <div class="px-5 py-1.5 bg-gray-50 sticky top-0">
                  <span class="text-xs font-semibold text-gray-400 uppercase tracking-wide">From recent</span>
                </div>
                <div
                  v-for="(entity, index) in matchingRecents"
                  :id="'search-result-recent-' + entity.id"
                  :key="'recent-match-' + entity.id"
                  role="option"
                  :aria-selected="selectedIndex === index"
                  class="flex items-center px-5 py-2.5 cursor-pointer transition-colors"
                  :class="selectedIndex === index ? 'bg-primary-50' : 'hover:bg-gray-50'"
                  @click="handleClick(entity)"
                  @mouseover="selectedIndex = index"
                >
                  <font-awesome-icon
                    :icon="getEntityIcon(entity)"
                    class="w-4 h-4 text-gray-400 shrink-0 mr-3"
                    aria-hidden="true"
                  />
                  <div class="flex flex-col min-w-0 flex-1">
                    <span
                      class="sensitive text-sm font-medium text-gray-800 truncate"
                      v-html="highlightMatch(entity.name, searchQuery)"
                    ></span>
                    <span
                      v-if="entity.breadcrumb && entity.breadcrumb.length > 0"
                      class="text-xs text-gray-400 truncate mt-0.5 inline-flex items-center gap-0.5"
                    >
                      <template v-for="(ancestor, bIdx) in entity.breadcrumb" :key="'rmatch-bc-' + bIdx">
                        <span v-if="bIdx > 0" class="text-gray-300 mx-0.5">›</span>
                        <font-awesome-icon
                          :icon="getEntityIcon({ entity_type_name: ancestor.type_name })"
                          class="w-2.5 h-2.5 opacity-50 shrink-0"
                          aria-hidden="true"
                        />
                        <span class="sensitive truncate">{{ ancestor.name }}</span>
                      </template>
                    </span>
                  </div>
                  <span class="shrink-0 ml-3 px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-500 rounded-full">
                    {{ formatEntityTypeName(entity.entity_type_name || 'Entity') }}
                  </span>
                </div>
              </div>

              <!-- No results -->
              <div v-else-if="displayResults.length === 0 && !isLoading" class="px-5 py-12 text-center">
                <p v-if="searchQuery" class="text-sm text-gray-500">No results for "{{ searchQuery }}"</p>
                <p v-else-if="activeTypeFilter" class="text-sm text-gray-500">No {{ formatEntityTypeName(activeTypeFilter) }} entities found</p>
                <p v-else class="text-sm text-gray-500">No results</p>
                <p class="text-xs text-gray-400 mt-1">Try a different spelling or broader search term</p>
              </div>

              <!-- Hierarchical tree results (with optimistic UI) -->
              <div v-else :class="{ 'opacity-50': isLoading && previousResults.length > 0 }">
                <div
                  v-for="(result, index) in displayResults"
                  :id="'search-result-' + result.id"
                  :key="'flat-' + result.id"
                  role="option"
                  :aria-selected="selectedIndex === index"
                  class="flex items-center px-5 py-2.5 cursor-pointer transition-colors"
                  :class="selectedIndex === index ? 'bg-primary-50' : 'hover:bg-gray-50'"
                  @click="handleClick(result)"
                  @mouseover="selectedIndex = index"
                >
                  <font-awesome-icon
                    :icon="getEntityIcon(result)"
                    class="w-4 h-4 text-gray-500 shrink-0 mr-2.5"
                    aria-hidden="true"
                  />
                  <div class="flex flex-col min-w-0 flex-1">
                    <span
                      class="sensitive text-sm font-medium text-gray-800 truncate"
                      v-html="highlightMatch(result.name, searchQuery)"
                    ></span>
                    <span
                      v-if="result.breadcrumb && result.breadcrumb.length > 0"
                      class="text-xs text-gray-400 truncate mt-0.5 inline-flex items-center gap-0.5"
                    >
                      <template v-for="(ancestor, bIdx) in result.breadcrumb" :key="'bc-' + bIdx">
                        <span v-if="bIdx > 0" class="text-gray-300 mx-0.5">›</span>
                        <font-awesome-icon
                          :icon="getEntityIcon({ entity_type_name: ancestor.type_name })"
                          class="w-2.5 h-2.5 opacity-50 shrink-0"
                          aria-hidden="true"
                        />
                        <span class="sensitive truncate">{{ ancestor.name }}</span>
                      </template>
                    </span>
                    <span
                      v-else-if="getMatchContext(result) && !isVagueMatch(result)"
                      class="text-xs text-gray-400 truncate mt-0.5"
                      v-html="getMatchContext(result)"
                    ></span>
                  </div>
                  <span
                    v-if="isVagueMatch(result)"
                    class="shrink-0 ml-2 px-1.5 py-0.5 text-[10px] italic text-gray-400 bg-gray-50 border border-gray-200 rounded-full"
                  >
                    related match
                  </span>
                  <span
                    v-if="!activeTypeFilter"
                    class="shrink-0 ml-3 px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-500 rounded-full"
                  >
                    {{ formatEntityTypeName(result.entity_type_name || 'Entity') }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Live region for screen readers -->
            <div aria-live="polite" aria-atomic="true" class="sr-only sensitive">
              {{ liveAnnouncement }}
            </div>

            <!-- Footer -->
            <div v-if="displayResults.length > 0 || showRecents" class="flex items-center justify-between px-4 py-1.5 border-t border-gray-100 bg-gray-50 text-[10px] text-gray-400 rounded-b-xl">
              <div class="flex items-center gap-2">
                <span><kbd class="px-1 py-0.5 bg-white border border-gray-200 rounded text-[10px]">↑↓</kbd> navigate</span>
                <span><kbd class="px-1 py-0.5 bg-white border border-gray-200 rounded text-[10px]">↵</kbd> select</span>
                <span><kbd class="px-1 py-0.5 bg-white border border-gray-200 rounded text-[10px]">esc</kbd> close</span>
              </div>
              <span>
                <template v-if="isLoading && displayResults.length > 0">
                  <svg class="animate-spin inline h-3 w-3 text-gray-400 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Searching...
                </template>
                <template v-else-if="showRecents">
                  {{ recentEntities.length }} recent
                </template>
                <template v-else>
                  {{ displayResults.length }} result{{ displayResults.length !== 1 ? 's' : '' }}
                </template>
              </span>
            </div>
          </div>
      </Transition>

    <!-- Search tips popover -->
    <Teleport to="body">
      <Transition name="search-tips">
        <div
          v-if="showSearchTips"
          ref="searchTipsPopover"
          class="fixed z-[110] w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg"
          :style="searchTipsPosition"
          @mouseenter="showSearchTips = true"
          @mouseleave="showSearchTips = false"
        >
          <p class="font-semibold mb-1.5">Search tips</p>
          <p class="mb-1.5">Type a category name then press <kbd class="px-1 py-0.5 bg-gray-700 rounded text-[10px]">Tab</kbd> to filter by type:</p>
          <div class="flex flex-wrap gap-1 mb-1.5">
            <span
              v-for="t in entityTypes"
              :key="'tip-' + t.id"
              class="px-1.5 py-0.5 bg-gray-700 rounded text-[10px]"
            >{{ formatEntityTypeName(t.type_name) }}</span>
          </div>
          <p class="text-gray-400">Press <kbd class="px-1 py-0.5 bg-gray-700 rounded text-[10px]">Backspace</kbd> to clear the filter.</p>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script>
import axios from 'axios'
import { formatEntityTypeName, getPinnedTypes } from '@/utils/entityTypeFilters'
import { debounce } from 'lodash'
import { canAccessEntity } from '@/utils/access'
import { getEntityIcon } from '@/utils/entityIcons'
import EntityTypeFilters from './EntityTypeFilters.vue'
import demoProtectedFields from '@/mixins/demoProtectedFields'

const RECENT_ENTITIES_KEY_PREFIX = 'entitySearch.recentEntities'
const MAX_RECENTS = 10

const HTML_ESCAPE_MAP = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }
const HTML_ESCAPE_RE = /[&<>"']/g
function escapeHtml(text) {
  return String(text).replace(HTML_ESCAPE_RE, (c) => HTML_ESCAPE_MAP[c])
}

export default {
  components: { EntityTypeFilters },
  mixins: [demoProtectedFields],
  // The global entity search (patients, providers and practices by name or ID) identifies real patients, providers and practices. Demo Mode empties it and the tagged
  // input is read-only while protected. It is not persisted.
  demoProtectedFields: ['search_text'],
  data() {
    return {
      search_text: '',
      search_results: [],
      previousResults: [],
      modalOpen: false,
      dropdownOpen: false,
      selectedIndex: 0,
      entityTypes: [],
      isLoading: false,
      recentEntities: [],
      liveAnnouncement: '',
      collapsedNodes: new Set(),
      activeTypeFilter: null,
      showSearchTips: false,
      searchTipsPosition: {},
    }
  },
  computed: {
    isMac() {
      return /mac/i.test(navigator.userAgentData?.platform || navigator.platform || '')
    },
    tenantSlug() {
      return this.$store.getters['tenant/current_tenant_slug'] || 'default'
    },
    currentEntityId() {
      return this.$store.getters['entity/current_entity_id'] || null
    },
    recentsStorageKey() {
      return `${RECENT_ENTITIES_KEY_PREFIX}.${this.tenantSlug}`
    },
    tabCompleteSuggestion() {
      const text = this.search_text.trim().toLowerCase()
      if (!text || text.length < 2 || this.activeTypeFilter) return null
      const match = this.entityTypes.find(
        (t) => t.type_name.toLowerCase().startsWith(text) && t.type_name.toLowerCase() !== text,
      )
      if (match) return match.type_name
      const exact = this.entityTypes.find((t) => t.type_name.toLowerCase() === text)
      return exact ? exact.type_name : null
    },
    searchQuery() {
      return this.search_text.trim()
    },
    typeAutocompleteSuggestions() {
      const text = this.search_text.trim().toLowerCase()
      if (!text || text.length < 1 || this.activeTypeFilter) return []
      // Exclude whatever's already pinned inline above (EntityTypeFilters) so
      // the same type doesn't show as both a pinned pill and a "Filter by:" pill.
      const pinnedIds = new Set(getPinnedTypes(this.entityTypes).map((t) => t.id))
      return this.entityTypes.filter(
        (t) => !pinnedIds.has(t.id) &&
          (t.type_name.toLowerCase().startsWith(text) ||
            t.type_name.toLowerCase().includes(text)),
      ).slice(0, 5)
    },
    effectiveTypeIds() {
      if (this.activeTypeFilter) {
        const match = this.entityTypes.find((t) => t.type_name === this.activeTypeFilter)
        return match ? [match.id] : []
      }
      return []
    },
    displayResults() {
      const source = this.search_results.length > 0
        ? this.search_results
        : this.previousResults
      const filtered = []
      const seenIds = new Set()
      for (const result of source) {
        if (filtered.length >= 25) break
        if (!result.name || !result.name.trim()) continue
        if (!canAccessEntity(result.id)) continue
        if (seenIds.has(result.id)) continue
        seenIds.add(result.id)
        filtered.push(result)
      }
      return filtered
    },
    matchingRecents() {
      const query = this.searchQuery.toLowerCase()
      if (!query || query.length < 2) return []
      return this.recentEntities.filter((e) => {
        if (!e.name || !e.name.toLowerCase().includes(query)) return false
        if (this.activeTypeFilter && e.entity_type_name !== this.activeTypeFilter) return false
        return true
      })
    },
    showRecents() {
      return (
        (!this.search_text || this.search_text.length < 2) &&
        !this.isLoading &&
        !this.activeTypeFilter &&
        this.recentEntities.length > 0
      )
    },
    treeRows() {
      const results = this.displayResults
      if (results.length === 0) return []

      // --- Phase 1: Build a canonical node registry keyed by entity ID ---
      const nodeById = new Map()
      const placedResultIds = new Set()

      const getOrCreate = (id, name, typeName, isResult, resultData) => {
        if (nodeById.has(id)) {
          const entry = nodeById.get(id)
          if (isResult && !entry.node._isResult) {
            entry.node = { ...resultData, _isContext: false, _isResult: true }
            placedResultIds.add(resultData.id)
          }
          return entry
        }
        const entry = {
          node: isResult
            ? { ...resultData, _isContext: false, _isResult: true }
            : { id, name, type_name: typeName, entity_type_name: typeName, _isContext: true, _isResult: false },
          children: new Map(),
        }
        nodeById.set(id, entry)
        if (isResult) placedResultIds.add(resultData.id)
        return entry
      }

      // --- Phase 2: Place each result into a tree via its breadcrumb ---
      const roots = new Map()

      for (const result of results) {
        if (placedResultIds.has(result.id)) continue

        const path = result.breadcrumb?.length > 0 ? result.breadcrumb : []
        let currentChildren = roots

        for (const ancestor of path) {
          const entry = getOrCreate(
            ancestor.id, ancestor.name, ancestor.type_name,
            false, null,
          )
          if (!currentChildren.has(ancestor.id)) currentChildren.set(ancestor.id, entry)
          currentChildren = entry.children
        }

        if (placedResultIds.has(result.id)) continue

        const entry = getOrCreate(
          result.id, result.name, result.entity_type_name,
          true, result,
        )
        if (!currentChildren.has(result.id)) currentChildren.set(result.id, entry)
      }

      // --- Phase 3: Flatten tree to rows, respecting collapse state ---
      const rows = []
      let flatIndex = 0
      const collapsed = this.collapsedNodes

      const visited = new Set()
      const walk = (children, depth, rootId) => {
        if (depth > 20) return
        for (const [, entry] of children) {
          if (visited.has(entry.node.id)) continue
          visited.add(entry.node.id)

          const hasChildren = entry.children.size > 0
          const isContext = entry.node._isContext
          const isCollapsed = hasChildren && isContext && collapsed.has(entry.node.id)

          const node = {
            ...entry.node,
            _depth: depth,
            _key: entry.node.id + '-' + depth,
            _rootId: rootId || entry.node.id,
            _hasChildren: hasChildren,
            _isCollapsed: isCollapsed,
          }
          delete node._isResult

          if (!isContext) node._flatIndex = flatIndex++
          rows.push(node)

          if (!isCollapsed && hasChildren) {
            walk(entry.children, depth + 1, rootId || entry.node.id)
          }
        }
      }
      walk(roots, 0, null)

      return rows
    },
    flatResults() {
      return this.treeRows.filter((r) => !r._isContext)
    },
    navigableItems() {
      if (this.showRecents) return this.recentEntities
      if (this.displayResults.length === 0 && this.matchingRecents.length > 0) return this.matchingRecents
      return this.displayResults
    },
    activeDescendantId() {
      if (this.selectedIndex < 0) return undefined
      if (this.showRecents && this.recentEntities[this.selectedIndex]) {
        return 'search-result-recent-' + this.recentEntities[this.selectedIndex].id
      }
      const item = this.displayResults[this.selectedIndex]
      return item ? 'search-result-' + item.id : undefined
    },
  },
  watch: {
    displayResults(newVal) {
      this._matchContextCache = null
      this.debouncedAnnounce(newVal)
    },
    navigableItems(items) {
      if (this.selectedIndex >= items.length) {
        this.selectedIndex = Math.max(0, items.length - 1)
      }
    },
    tenantSlug() {
      this.loadRecents()
    },
    showSearchTips(val) {
      if (val) {
        this.$nextTick(() => this.positionSearchTips())
      }
    },
  },
  created() {
    this.debouncedSearch = debounce(this.searchEntities, 300)
    this.debouncedAnnounce = debounce((results) => {
      if (this.showRecents) {
        this.liveAnnouncement = `${this.recentEntities.length} recent entities`
      } else if (results.length > 0) {
        this.liveAnnouncement = `${results.length} results`
      } else if (this.search_text.length >= 2 && !this.isLoading) {
        this.liveAnnouncement = `No results for ${this.searchQuery}`
      }
    }, 500)
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeyDown)
    document.addEventListener('mousedown', this.onClickOutside)
    this.loadEntityTypes()
    this.loadRecents()
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown)
    document.removeEventListener('mousedown', this.onClickOutside)
  },
  methods: {
    formatEntityTypeName,
    getEntityIcon,
    openModal() {
      this.modalOpen = true
      this.dropdownOpen = true
      this.$nextTick(() => {
        this.$refs.modalSearchInput?.focus()
        if (this.recentEntities.length > 0) {
          this.liveAnnouncement = `${this.recentEntities.length} recent entities`
        }
      })
    },
    closeModal() {
      this.modalOpen = false
      this.dropdownOpen = false
      this.search_text = ''
      this.search_results = []
      this.previousResults = []
      this.selectedIndex = 0
      this.isLoading = false
      this.activeTypeFilter = null
      this.showSearchTips = false
    },
    onInputFocus() {
      this.dropdownOpen = true
      this.modalOpen = true
      if (this.recentEntities.length > 0 && !this.search_text) {
        this.liveAnnouncement = `${this.recentEntities.length} recent entities`
      }
    },
    onClickOutside(event) {
      if (this.$refs.searchComponent && !this.$refs.searchComponent.contains(event.target)) {
        this.closeModal()
      }
    },
    clearSearch() {
      this.search_text = ''
      this.search_results = []
      this.previousResults = []
      this.selectedIndex = 0
      this.activeTypeFilter = null
      this.$refs.modalSearchInput?.focus()
    },
    async searchEntities(query) {
      if ((!query || query.length < 2) && !this.activeTypeFilter) {
        this.search_results = []
        this.isLoading = false
        return
      }

      this.isLoading = true
      try {
        const params = {}
        const searchTerm = this.searchQuery

        if (searchTerm || this.activeTypeFilter) {
          params.search = searchTerm || ''
        }

        const typeIds = this.effectiveTypeIds
        const allSelected = this.entityTypes.length > 0 && typeIds.length === this.entityTypes.length
        if (!allSelected && typeIds.length > 0) {
          params.entity_type_ids = typeIds.join(',')
        }

        if (this.currentEntityId) {
          params.context_entity_id = this.currentEntityId
        }

        const response = await axios.get('api/entities/search/', { params })
        this.search_results = response.data.results || response.data
        this.previousResults = [...this.search_results]
        this.collapsedNodes = new Set()
      } catch (error) {
        console.error('Error searching entities', error)
        this.search_results = []
      } finally {
        this.isLoading = false
      }
    },
    onSearch() {
      if (this.search_text.length >= 2 || this.activeTypeFilter) {
        this.isLoading = true
        this.search_results = []
        this.debouncedSearch(this.search_text)
        this.selectedIndex = 0
      } else {
        this.search_results = []
        this.selectedIndex = 0
      }
    },
    handleClick(result) {
      this.addToRecents(result)
      this.closeModal()
      this.$router.push({
        name: 'Entity',
        params: { entity_id: result.id },
      })
    },
    addToRecents(entity) {
      const entry = {
        id: entity.id,
        name: entity.name,
        entity_type_name: entity.entity_type_name,
        breadcrumb: entity.breadcrumb || [],
      }
      const filtered = this.recentEntities.filter((e) => e.id !== entity.id)
      filtered.unshift(entry)
      this.recentEntities = filtered.slice(0, MAX_RECENTS)
      this.saveRecents()
    },
    loadRecents() {
      try {
        const stored = localStorage.getItem(this.recentsStorageKey)
        if (stored) {
          const parsed = JSON.parse(stored)
          if (Array.isArray(parsed)) {
            this.recentEntities = parsed.slice(0, MAX_RECENTS)
          }
        }
      } catch {
        this.recentEntities = []
      }
    },
    saveRecents() {
      try {
        localStorage.setItem(this.recentsStorageKey, JSON.stringify(this.recentEntities))
      } catch {
        // Safari private mode / quota exceeded
      }
    },
    clearRecents() {
      this.recentEntities = []
      localStorage.removeItem(this.recentsStorageKey)
      this.selectedIndex = 0
    },
    removeRecent(entityId) {
      this.recentEntities = this.recentEntities.filter((e) => e.id !== entityId)
      this.saveRecents()
      if (this.selectedIndex >= this.recentEntities.length) {
        this.selectedIndex = Math.max(0, this.recentEntities.length - 1)
      }
    },
    async loadEntityTypes() {
      try {
        const response = await axios.get('api/entity-types/')
        const types = (response.data.results || response.data).sort((a, b) =>
          a.type_name.localeCompare(b.type_name),
        )
        this.entityTypes = types
      } catch (error) {
        console.error('Error loading entity types', error)
      }
    },
    focusSearch() {
      this.$refs.modalSearchInput?.focus()
      this.dropdownOpen = true
      this.modalOpen = true
    },
    handleKeyDown(event) {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault()
        this.focusSearch()
      }
    },
    toggleCollapse(nodeId) {
      if (this.collapsedNodes.has(nodeId)) {
        this.collapsedNodes.delete(nodeId)
      } else {
        this.collapsedNodes.add(nodeId)
      }
      // Force reactivity since Set mutations aren't tracked
      this.collapsedNodes = new Set(this.collapsedNodes)
    },
    tryTabCompleteType() {
      const text = this.search_text.trim().toLowerCase()
      if (!text || this.activeTypeFilter) return false
      const match = this.entityTypes.find(
        (t) => t.type_name.toLowerCase() === text ||
          t.type_name.toLowerCase().startsWith(text),
      )
      if (match) {
        this.setTypeFilter(match.type_name)
        return true
      }
      return false
    },
    clearTypeFilter() {
      this.activeTypeFilter = null
      this.previousResults = []
      this.onSearch()
      this.$nextTick(() => this.$refs.modalSearchInput?.focus())
    },
    setTypeFilter(typeName, preserveSearch = false) {
      this.activeTypeFilter = typeName
      this.search_results = []
      this.previousResults = []
      this.selectedIndex = 0
      if (preserveSearch && this.search_text.trim().length >= 2) {
        this.onSearch()
      } else {
        this.search_text = ''
        this.loadFilteredResults()
      }
      this.$nextTick(() => this.$refs.modalSearchInput?.focus())
    },
    async loadFilteredResults() {
      this.isLoading = true
      try {
        const params = { search: '' }
        const typeIds = this.effectiveTypeIds
        if (typeIds.length > 0) {
          params.entity_type_ids = typeIds.join(',')
        }
        if (this.currentEntityId) {
          params.context_entity_id = this.currentEntityId
        }
        const response = await axios.get('api/entities/search/', { params })
        this.search_results = response.data.results || response.data
        this.previousResults = [...this.search_results]
        this.collapsedNodes = new Set()
      } catch (error) {
        console.error('Error loading filtered results', error)
        this.search_results = []
      } finally {
        this.isLoading = false
      }
    },
    handleSearchKeydown(event) {
      if (event.key === 'Tab') {
        if (this.tryTabCompleteType()) {
          event.preventDefault()
          return
        }
      }

      if (event.key === 'Backspace' && !this.search_text && this.activeTypeFilter) {
        event.preventDefault()
        this.clearTypeFilter()
        return
      }

      const items = this.navigableItems
      const total = items.length

      switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        if (total > 0) {
          this.selectedIndex = (this.selectedIndex + 1) % total
          this.ensureSelectedIsVisible()
        }
        break
      case 'ArrowUp':
        event.preventDefault()
        if (total > 0) {
          this.selectedIndex = this.selectedIndex <= 0 ? total - 1 : this.selectedIndex - 1
          this.ensureSelectedIsVisible()
        }
        break
      case 'ArrowLeft':
        if (!this.search_text) {
          event.preventDefault()
          this.collapseCurrentParent()
        }
        break
      case 'ArrowRight':
        if (!this.search_text) {
          event.preventDefault()
          this.expandCurrentParent()
        }
        break
      case 'Enter':
        event.preventDefault()
        if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
          this.handleClick(items[this.selectedIndex])
        }
        break
      case 'Escape':
        event.preventDefault()
        this.closeModal()
        break
      }
    },
    collapseCurrentParent() {
      if (this.showRecents) return
      const current = this.flatResults[this.selectedIndex]
      if (!current || !current._rootId) return
      if (!this.collapsedNodes.has(current._rootId)) {
        this.toggleCollapse(current._rootId)
      }
    },
    expandCurrentParent() {
      if (this.showRecents) return
      const current = this.flatResults[this.selectedIndex]
      if (!current || !current._rootId) return
      if (this.collapsedNodes.has(current._rootId)) {
        this.toggleCollapse(current._rootId)
      }
    },
    handleTabKey(event) {
      const modal = this.$refs.modalContainer
      if (!modal) return

      const focusable = modal.querySelectorAll(
        'input, button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )
      if (focusable.length === 0) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      if (event.shiftKey) {
        if (document.activeElement === first) {
          event.preventDefault()
          last.focus()
        }
      } else {
        if (document.activeElement === last) {
          event.preventDefault()
          first.focus()
        }
      }
    },
    ensureSelectedIsVisible() {
      this.$nextTick(() => {
        const container = this.$refs.resultsContainer
        if (!container) return
        const selected = container.querySelector('[aria-selected="true"]')
        if (selected) {
          selected.scrollIntoView({ block: 'nearest' })
        }
      })
    },
    matchContext(result) {
      const query = this.searchQuery
      if (!query || query.length < 2) return null

      if (result.matched_field && result.matched_field !== 'name') {
        const label = result.matched_field.replace(/_/g, ' ')
        const value = result.matched_value || ''
        return `${escapeHtml(label)}: ${this.highlightMatch(value, query)}`
      }

      if (result.name && result.name.toLowerCase().includes(query.toLowerCase())) return null

      const fields = [
        ['address', result.address],
        ['NPI', result.npi],
        ['TIN', result.tin],
        ['identifier', result.identifier],
      ]

      const skip = new Set(['id', 'name', 'entity_type', 'entity_type_name', 'breadcrumb', '_flatIndex', '_depth', '_key', '_isContext', '_rootId', '_hasChildren', '_isCollapsed', '_showBreadcrumb', 'icon', 'entity_icon', 'matched_field', 'matched_value', 'similarity'])
      for (const [key, val] of Object.entries(result)) {
        if (skip.has(key) || typeof val !== 'string' || !val) continue
        if (val.toLowerCase().includes(query.toLowerCase())) {
          fields.push([key.replace(/_/g, ' '), val])
          break
        }
      }

      for (const [label, value] of fields) {
        if (!value) continue
        const strVal = String(value)
        if (strVal.toLowerCase().includes(query.toLowerCase())) {
          return `${escapeHtml(label)}: ${this.highlightMatch(strVal, query)}`
        }
      }

      if (result.similarity && result.similarity > 0) {
        return `<span class="italic">fuzzy match (${Math.round(result.similarity * 100)}% similar)</span>`
      }
      return `<span class="italic">matched on related data</span>`
    },
    isVagueMatch(node) {
      const ctx = this.getMatchContext(node)
      return ctx && ctx.includes('matched on related data')
    },
    getMatchContext(node) {
      if (!this._matchContextCache) this._matchContextCache = new WeakMap()
      if (this._matchContextCache.has(node)) return this._matchContextCache.get(node)
      const result = this.matchContext(node)
      this._matchContextCache.set(node, result)
      return result
    },
    positionSearchTips() {
      const trigger = this.$refs.searchTipsTrigger
      if (!trigger) return
      const rect = trigger.getBoundingClientRect()
      this.searchTipsPosition = {
        top: rect.bottom + 8 + 'px',
        right: Math.max(8, window.innerWidth - rect.right) + 'px',
      }
    },
    highlightMatch(text, query) {
      if (!query || query.length < 2) return escapeHtml(text)
      const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const regex = new RegExp(`(${escaped})`, 'gi')
      return escapeHtml(text).replace(
        regex,
        '<mark class="bg-yellow-100 text-inherit rounded-sm">$1</mark>',
      )
    },
  },
}
</script>

<style scoped>
@reference "../../../assets/theme.css";

.search-dropdown-enter-active,
.search-dropdown-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.search-dropdown-enter-from,
.search-dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.search-tips-enter-active,
.search-tips-leave-active {
  transition: opacity 0.15s ease;
}

.search-tips-enter-from,
.search-tips-leave-to {
  opacity: 0;
}
</style>
